import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export interface AxialSlotDiagramCopy {
  sectionTitle: string;
  sectionLead: string;
}

export interface AxialSlotPageData {
  content: SubPageContent;
  diagramCopy: AxialSlotDiagramCopy;
}

export const COPY: Record<Lang, AxialSlotPageData> = {
  en: {
    diagramCopy: {
      sectionTitle: "Axial Slot Mechanical Architecture",
      sectionLead: "Surface profile wires run along the longitudinal axis of the cylinder, providing smooth continuous tracks that allow mechanical scraper blades to travel without crossing wire edges.",
    },
    content: {
      breadcrumbKey: "AxialSlotCylinders",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Wedge Wire Cylinders",
        standardBadge: "Longitudinal V-Wire",
        heading: "Axial Slot Cylinders",
        lead: "Specialized wedge wire filter cylinders with continuous slots running parallel to the longitudinal axis. Engineered specifically for automatic mechanical scraper cleaning, high-differential radial flow straining, and internal or external solid separation.",
        secondaryCta: {
          label: "View All Cylindrical Screens",
          to: "/CylindricalScreensPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
          alt: "Axial slot wedge wire cylindrical screen with longitudinal profile wire arrangement",
        },
        cardNote: "Scraper Systems · FOTI / FITO",
        cardLink: {
          label: "Request a Quote",
          to: "/RequestAQuotePage",
        },
        specs: [
          { label: "Slot Width", value: "0.02 – 5.0 mm" },
          { label: "Orientation", value: "Parallel to Axis" },
          { label: "Flow Direction", value: "FOTI / FITO Options" },
          { label: "Standard Alloys", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Linear Surface Guide & Scraper Protection",
        heading: "Longitudinal V-Wire Orientation for Mechanical Scraper Systems",
        paragraphs: [
          "MutualFilter axial slot cylinders orient the V-shaped profile wires parallel to the cylinder's longitudinal axis, supported underneath by circular circumferential reinforcement rings. This alignment creates uninterrupted linear tracks along the length of the cylinder, allowing reciprocating or rotary scraper blades to clear filtered cakes without chattering or snagging across wire gaps.",
          "In scraper-driven filtration vessels, circumferential slot orientations would subject blade tips to thousands of wire edge impacts per stroke. Axial alignment eliminates blade chatter, extends scraper wiper life, and prevents premature mechanical fatigue on the weld joints during continuous de-caking cycles.",
          "Axial cylinders can be configured with the smooth working face either on the exterior (Flow-Out-To-In, FOTI) or on the interior (Flow-In-To-Out, FITO). Internal scraper baskets are widely used in paint, resin, starch, and chocolate processing, while external scraping is typical in self-cleaning intake water strainers.",
        ],
        keyPoints: [
          "Longitudinal profile wires provide a continuous linear track that protects scraper blades from impact and excessive wear.",
          "Circumferential internal or external support rings maintain precise slot spacing under intense scraper drive torque.",
          "Available with smooth interior or exterior surfaces for versatile inward or outward process flow geometries.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
          alt: "Axial slot wedge wire cylinder showing longitudinal profile wires and support rings",
        },
      },
      specTable: {
        heading: "Axial Slot Cylinder Engineering Specifications",
        intro: "Engineered to custom lengths and diameters for seamless integration into self-cleaning filter housings and centrifuge baskets.",
        columns: [
          { key: "parameter", label: "Specification", align: "left" },
          { key: "range", label: "Standard Range", align: "left" },
          { key: "details", label: "Engineering Details", align: "left" },
        ],
        rows: [
          {
            parameter: "Slot Width",
            range: "0.02 – 5.0 mm",
            details: "Continuous longitudinal aperture with uniform micro-tolerances",
          },
          {
            parameter: "Orientation",
            range: "Parallel to Axis",
            details: "Wedge wires aligned longitudinally with circular transverse support rings",
          },
          {
            parameter: "Flow Configurations",
            range: "FOTI / FITO",
            details: "Flow-Out-To-In (external scraper) or Flow-In-To-Out (internal scraper blade)",
          },
          {
            parameter: "Standard Alloys",
            range: "AISI 304, AISI 316, 316L",
            details: "High-grade stainless steel for sanitary, chemical, and industrial service",
          },
          {
            parameter: "Diameter Range",
            range: "25.4 – 1000 mm",
            details: "Custom fabricated to match existing automatic strainer vessel dimensions",
          },
        ],
        caption: "Parameters reflect standard precision manufacturing capabilities for axial wedge wire screens.",
      },
      features: {
        heading: "Design Advantages of Axial Slots",
        intro: "Purpose-built for systems combining continuous filtration with automated mechanical cake removal.",
        items: [
          {
            title: "Zero-Chatter Scraping",
            text: "Linear wire tracks prevent blade vibration, edge chipping, and uneven solids removal.",
          },
          {
            title: "Reinforced Support Rings",
            text: "Heavy-gauge internal or external rings provide hoop strength against collapse and deflection.",
          },
          {
            title: "Custom End Connections",
            text: "Flanges, machined seal faces, and keyway drive rings welded to exact equipment tolerances.",
          },
          {
            title: "High Cleanability",
            text: "Smooth profile slots resist biological fouling and clean completely during automated backwash cycles.",
          },
        ],
      },
      related: {
        heading: "Related Wedge Wire Cylinders",
        intro: "Compare axial slot architecture with circumferential slot cylinders or return to the parent category.",
        cards: [
          {
            title: "Circumferential Slot Cylinders",
            text: "Surface wires run continuously around the circumference of the cylinder with axial support rods, engineered for rotary drums, trommels, and internal-feed screening.",
            to: "/CircumferentialSlotCylindersPage",
            badge: "Around Circumference",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
              alt: "Stainless steel circumferential slot cylinder screen with continuous radial wedge wire for rotary trommels",
            },
            linkLabel: "View Circumferential Cylinders",
          },
          {
            title: "Cylindrical Screens Overview",
            text: "Explore the full spectrum of precision resistance-welded cylindrical wedge wire screens, manufacturing limits, and standard alloy options.",
            to: "/CylindricalScreensPage",
            badge: "Parent Category",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
              alt: "Industrial cylindrical stainless steel wedge wire filter screens and support cores",
            },
            linkLabel: "View Cylindrical Screens",
          },
        ],
      },
      cta: {
        id: "axial-slot-quote",
        eyebrow: "Engineering Inquiry",
        heading: "Request Axial Slot Cylinder Specifications & Quote",
        lead: "Provide your cylinder diameter, length, slot width, and scraper mechanism type for a tailored engineering review.",
        secondaryLabel: "Return to Cylindrical Screens",
        secondaryTo: "/CylindricalScreensPage",
        note: "MTR and positive material identification available when specified in the order.",
      },
    },
  },
  zh: {
    diagramCopy: {
      sectionTitle: "轴向缝隙机械工作原理示意图",
      sectionLead: "V 型轮廓丝沿滤筒轴向平行排列，为机械刮刀提供平滑连续的直线导向，彻底避免刮刀反复横跨切断筛条缝隙带来的磨损与震颤。",
    },
    content: {
      breadcrumbKey: "AxialSlotCylinders",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "圆柱楔形丝滤筒",
        standardBadge: "纵向轴向 V 型丝",
        heading: "轴向缝隙滤筒",
        lead: "专为机械刮刀自清洗系统打造的轴向缝隙楔形丝滤筒。筛条沿滤筒中心纵轴平行排列，提供平整通畅的直线轨道，有效杜绝刮渣颤抖与刃口磨损，满足高负荷流体过滤与固液分离要求。",
        secondaryCta: {
          label: "查看圆柱筛筒总览",
          to: "/CylindricalScreensPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
          alt: "轴向缝隙楔形丝圆柱筛筒与平行排列结构",
        },
        cardNote: "刮刀自清洗 · FOTI / FITO",
        cardLink: {
          label: "获取工程报价",
          to: "/RequestAQuotePage",
        },
        specs: [
          { label: "缝隙宽度", value: "0.02 – 5.0 mm" },
          { label: "缝隙走向", value: "轴向平行" },
          { label: "流向配置", value: "FOTI / FITO 可选" },
          { label: "标准材质", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "直线刮渣导向与刮刀保护",
        heading: "专为刮刀自清洗设备设计的纵向 V 型丝结构",
        paragraphs: [
          "MutualFilter 轴向缝隙滤筒采用 V 型型材丝平行于圆筒中心纵轴的排列方式，底部由圆周方向的环形加强筋提供刚性支撑。这种结构使滤筒内表面或外表面形成完全平行的直线轨道，无论是旋转刮刀还是轴向往复刮刀，均能在表面平顺滑动，彻底杜绝了刮刀横向越过缝隙边缘时产生的跳动与撞击。",
          "在自清洗刮刀过滤器中，若采用常规环向缝隙，刮刀在每次刮削冲程中都会撞击数千次缝隙边刃，极易造成刮刀刃口崩裂磨损及焊接接头的机械疲劳。轴向缝隙排列完全消除了这种机械冲击，成倍延长了刮刀和滤芯的使用周期。",
          "轴向滤筒可灵活定制为外表面平滑过滤（由外向内，FOTI）或内表面平滑过滤（由内向外，FITO）。内进料刮刀滤筒广泛应用于涂料、树脂、淀粉浆料及高粘度流体除杂，而外进料刮洗则常见于原水自清洗水过滤器与工业循环水处理。",
        ],
        keyPoints: [
          "轴向连续型材丝为自清洗刮刀提供平滑无阻的线性滑道，有效保护刮刀刃口免受冲击损坏。",
          "内部或外部全熔焊环形加强圈，在强扭矩机械刮渣和瞬态高差压下保持精确恒定的缝隙宽度。",
          "支持光洁内表面与光洁外表面两种制造构型，完美匹配由内向外或由外向内的各种工艺流向。",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
          alt: "显示轴向纵向排列 V 型丝与支撑环的楔形丝滤筒",
        },
      },
      specTable: {
        heading: "轴向缝隙滤筒技术参数表",
        intro: "支持根据过滤机壳体、驱动主轴及法兰标准进行全尺寸图纸定制。",
        columns: [
          { key: "parameter", label: "技术指标", align: "left" },
          { key: "range", label: "标准范围", align: "left" },
          { key: "details", label: "工程说明", align: "left" },
        ],
        rows: [
          {
            parameter: "缝隙宽度",
            range: "0.02 – 5.0 mm",
            details: "轴向纵向连续微米缝隙，全程保持严格一致的开孔公差",
          },
          {
            parameter: "缝隙走向",
            range: "轴向平行",
            details: "V 型轮廓丝沿轴心线纵向排列，支撑环沿圆周方向布置",
          },
          {
            parameter: "流向配置",
            range: "FOTI / FITO",
            details: "由外向内（外表面刮刀）或由内向外（内表面旋转刮刀）",
          },
          {
            parameter: "标准材质",
            range: "AISI 304, AISI 316, 316L",
            details: "高纯度不锈钢材质，满足工业耐蚀与食品卫生工艺标准",
          },
          {
            parameter: "直径范围",
            range: "25.4 – 1000 mm",
            details: "按客户自清洗过滤器内部工作腔体精准匹配制造",
          },
        ],
        caption: "数据代表轴向楔形丝标准精密电阻熔焊工艺制造能力。",
      },
      features: {
        heading: "轴向缝隙设计的核心优势",
        intro: "专为需要连续过滤且频繁依赖机械刮刀剥离滤饼的自动化工艺量身定制。",
        items: [
          {
            title: "无震颤刮渣运行",
            text: "纵向平行导向避免刮刀刃口跳动，实现彻底、均匀的高效滤饼清理。",
          },
          {
            title: "环向加强筋支撑",
            text: "高强度圆周支撑圈提供优异的抗压溃与抗径向变形能力。",
          },
          {
            title: "高精度端环组件",
            text: "一体熔焊机械加工法兰、卡槽定位环与密封止口，安装精准无偏摆。",
          },
          {
            title: "高效反洗再生",
            text: "无死角的梯形截面缝隙极易冲洗脱附，显著降低清洗水耗和系统维护频率。",
          },
        ],
      },
      related: {
        heading: "相关圆柱楔形丝产品",
        intro: "对比轴向缝隙与环向缝隙的区别，或返回圆柱形滤筒产品大类。",
        cards: [
          {
            title: "环向缝隙圆筒筛",
            text: "楔形丝沿圆筒圆周方向连续缠绕焊接，配置轴向支撑筋条，专为旋转转鼓筛、滚筒筛及内进料物料轴向推进分选而设计。",
            to: "/CircumferentialSlotCylindersPage",
            badge: "圆周环绕排列",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
              alt: "全周向环绕缝隙不锈钢圆筒筛网与转鼓分选结构",
            },
            linkLabel: "查看环向圆筒筛",
          },
          {
            title: "圆柱形楔形丝筛网总览",
            text: "全面了解全电阻熔焊圆柱楔形丝滤筒的整体技术规格、制造范围与标准合金配置。",
            to: "/CylindricalScreensPage",
            badge: "上级父分类",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
              alt: "工业级高精度不锈钢圆柱楔形丝滤筒与内部支撑骨架",
            },
            linkLabel: "返回圆柱筛筒总览",
          },
        ],
      },
      cta: {
        id: "axial-slot-quote",
        eyebrow: "轴向滤筒技术咨询",
        heading: "获取轴向缝隙滤筒规格确认与报价",
        lead: "请提供您的滤筒内外径尺寸、有效长度、缝隙精度及刮刀类型，工程师将为您核对制造参数。",
        secondaryLabel: "返回圆柱筛网分类",
        secondaryTo: "/CylindricalScreensPage",
        note: "订单明确要求时可按需提供材质检验报告与成分检测光谱分析。",
      },
    },
  },
  es: {
    diagramCopy: {
      sectionTitle: "Arquitectura Mecánica de Ranura Axial",
      sectionLead: "Los alambres perfilados corren a lo largo del eje longitudinal del cilindro, formando guías lineales continuas que permiten a las cuchillas raspadoras deslizarse sin cruzar aristas.",
    },
    content: {
      breadcrumbKey: "AxialSlotCylinders",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Cilindros de Alambre en Cuña",
        standardBadge: "Hilo en V Longitudinal",
        heading: "Cilindros de Ranura Axial",
        lead: "Cilindros filtrantes especializados de alambre en cuña con ranuras continuas paralelas al eje longitudinal. Diseñados para sistemas de raspado mecánico automático, retención de sólidos y filtración radial con mínima pérdida de carga.",
        secondaryCta: {
          label: "Ver Todos los Cilindros",
          to: "/CylindricalScreensPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
          alt: "Cilindro de alambre en cuña con ranura axial y perfiles longitudinales",
        },
        cardNote: "Sistemas Raspadores · FOTI / FITO",
        cardLink: {
          label: "Solicitar Presupuesto",
          to: "/RequestAQuotePage",
        },
        specs: [
          { label: "Ancho de ranura", value: "0.02 – 5.0 mm" },
          { label: "Orientación", value: "Paralela al eje" },
          { label: "Flujo", value: "Opciones FOTI / FITO" },
          { label: "Aleaciones estándar", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Guía Lineal Continua y Protección de Raspadores",
        heading: "Orientación Longitudinal de Alambre en V para Raspado Mecánico",
        paragraphs: [
          "Los cilindros de ranura axial MutualFilter orientan los alambres en V paralelamente al eje longitudinal del cilindro, con anillos de refuerzo circunferenciales en el lado opuesto. Esto genera pistas lineales suaves a lo largo de toda la longitud, permitiendo que las cuchillas raspadoras eliminen la torta sin vibración ni enganches.",
          "En filtros con raspador automático, una ranura circunferencial expondría el filo a continuos impactos contra los bordes del alambre. La orientación axial evita ese choque continuo, alargando la vida útil del raspador y previniendo la fatiga mecánica en las uniones soldadas.",
          "Disponibles con cara de trabajo lisa en el exterior (FOTI) o en el interior (FITO). La configuración interna es habitual en resinas, pinturas y fluidos viscosos, mientras que el raspado externo se utiliza en tomas de agua y filtros industriales de flujo continuo.",
        ],
        keyPoints: [
          "Los perfiles longitudinales proporcionan una guía lineal sin resaltes que protege las cuchillas contra el desgaste prematuro.",
          "Anillos de refuerzo circunferenciales que garantizan la estabilidad dimensional de la ranura bajo el par de giro del raspador.",
          "Opciones con superficie lisa interior o exterior para adaptarse a flujos centrípetos o centrífugos según el proceso.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
          alt: "Detalle de cilindro de ranura axial con perfiles longitudinales",
        },
      },
      specTable: {
        heading: "Especificaciones Técnicas de Cilindros Axiales",
        intro: "Fabricación según planos para integración en carcasas de filtros autolimpiantes y cestas centrífugas.",
        columns: [
          { key: "parameter", label: "Parámetro", align: "left" },
          { key: "range", label: "Rango Estándar", align: "left" },
          { key: "details", label: "Detalles Técnicos", align: "left" },
        ],
        rows: [
          {
            parameter: "Ancho de ranura",
            range: "0.02 – 5.0 mm",
            details: "Ranura continua longitudinal con tolerancias micrométricas uniformes",
          },
          {
            parameter: "Orientación",
            range: "Paralela al eje",
            details: "Alambres en V longitudinales con anillos circunferenciales de soporte",
          },
          {
            parameter: "Dirección de flujo",
            range: "FOTI / FITO",
            details: "Flujo de afuera hacia adentro o de adentro hacia afuera",
          },
          {
            parameter: "Aleaciones estándar",
            range: "AISI 304, AISI 316, 316L",
            details: "Acero inoxidable para aplicaciones industriales, químicas y sanitarias",
          },
          {
            parameter: "Diámetro",
            range: "25.4 – 1000 mm",
            details: "Dimensiones personalizadas para alojamiento en filtros automáticos",
          },
        ],
        caption: "Valores representativos para cilindros de ranura axial soldados por resistencia.",
      },
      features: {
        heading: "Ventajas del Diseño de Ranura Axial",
        intro: "Concebido para equipos de filtración continua combinados con sistemas automáticos de descolmatación.",
        items: [
          {
            title: "Raspado sin Vibración",
            text: "El deslizamiento lineal evita choques, rotura de filos y limpiezas irregulares.",
          },
          {
            title: "Anillos de Refuerzo Robustos",
            text: "Soportan altas cargas de colapso y el par mecánico transmitido por el raspador.",
          },
          {
            title: "Conexiones Mecanizadas",
            text: "Bridas y anillos de acoplamiento soldados con tolerancias estrictas de montaje.",
          },
          {
            title: "Excelente Retrolavado",
            text: "La ranura ensanchada hacia el interior permite una expulsión rápida de la torta retenida.",
          },
        ],
      },
      related: {
        heading: "Cilindros Relacionados",
        intro: "Compare la arquitectura de ranura axial con los cilindros de ranura circunferencial.",
        cards: [
          {
            title: "Cilindros de Ranura Circunferencial",
            text: "Los alambres se enrollan de forma continua alrededor de la circunferencia con varillas axiales de soporte, diseñados para tromeles rotativos y tambores de alimentación interna.",
            to: "/CircumferentialSlotCylindersPage",
            badge: "Circunferencial",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
              alt: "Cilindro de ranura circunferencial de alambre en cuña para tamizado rotativo",
            },
            linkLabel: "Ver Cilindros Circunferenciales",
          },
          {
            title: "Cilindros Filtrantes General",
            text: "Consulte todas las opciones de cilindros de alambre en cuña, límites de fabricación y variantes.",
            to: "/CylindricalScreensPage",
            badge: "Categoría Principal",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
              alt: "Telas filtrantes cilíndricas industriales de alambre en cuña de acero inoxidable",
            },
            linkLabel: "Volver a Cilindros Filtrantes",
          },
        ],
      },
      cta: {
        id: "axial-slot-quote",
        eyebrow: "Consulta Técnica",
        heading: "Solicite Cotización para Cilindros de Ranura Axial",
        lead: "Indique diámetro, longitud, abertura de ranura y tipo de mecanismo raspador a nuestro equipo de ingeniería.",
        secondaryLabel: "Volver a Cilindros Filtrantes",
        secondaryTo: "/CylindricalScreensPage",
        note: "Certificados de ensayo de materiales e identificación positiva de aleación disponibles bajo pedido.",
      },
    },
  },
  de: {
    diagramCopy: {
      sectionTitle: "Mechanisches Prinzip des Axialschlitzes",
      sectionLead: "Die Profildrähte verlaufen längs zur Zylinderachse und bilden glatte Führungsbahnen, auf denen Abstreiferklingen ohne Schlitzkantenschlag gleiten.",
    },
    content: {
      breadcrumbKey: "AxialSlotCylinders",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Spaltsiebzylinder",
        standardBadge: "Längsgerichteter V-Draht",
        heading: "Axialschlitz-Zylinder",
        lead: "Spezielle Spaltsiebzylinder mit kontinuierlichen, achsenparallelen Schlitzen. Speziell konstruiert für automatische Abstreiferfilter, hohe Differenzdrücke und radiale Fest-Flüssig-Trennung ohne Klingenrattern.",
        secondaryCta: {
          label: "Alle Zylinder ansehen",
          to: "/CylindricalScreensPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
          alt: "Axialschlitz-Spaltsiebzylinder mit achsenparallelem Profildraht",
        },
        cardNote: "Abstreifersysteme · FOTI / FITO",
        cardLink: {
          label: "Angebot anfordern",
          to: "/RequestAQuotePage",
        },
        specs: [
          { label: "Spaltweite", value: "0.02 – 5.0 mm" },
          { label: "Ausrichtung", value: "Parallel zur Achse" },
          { label: "Strömungsrichtung", value: "FOTI / FITO Optionen" },
          { label: "Standard-Legierungen", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Lineare Führungsbahn & Schaberschutz",
        heading: "Längsgerichtete V-Drähte für Mechanische Abstreifersysteme",
        paragraphs: [
          "MutualFilter Axialschlitzzylinder richten die V-förmigen Profildrähte achsenparallel aus, gestützt durch umlaufende Verstärkungsringe. Dies schafft unterbrechungsfreie lineare Bahnen entlang des Zylinders, sodass Abstreiferklingen den Filterkuchen sauber entfernen können, ohne an Drahtkanten hängenzubleiben.",
          "Bei Abstreiferfiltern mit Umfangsschlitzen würde die Klinge pro Hub tausende Male auf Drahtkanten aufschlagen. Die axiale Ausrichtung verhindert diesen Stoßverschleiß, schont die Schaberelemente und schützt die Schweißverbindungen vor vorzeitiger Ermüdung.",
          "Erhältlich mit glatter Arbeitsfläche wahlweise außen (FOTI) oder innen (FITO). Innenliegende Schaberkörbe sind ideal für Harze, Lacke und viskose Suspensionen, während externe Abreinigung bei selbstreinigenden Wassersieben üblich ist.",
        ],
        keyPoints: [
          "Längs verlaufende Profildrähte bieten eine glatte Bahn und verhindern Klingenrattern sowie Kantenverschleiß.",
          "Umlaufende Stützringe sichern die präzise Spaltweite auch bei hohem Drehmoment des Schabersystems.",
          "Ausführungen mit glatter Innen- oder Außenfläche für flexible Strömungsführungen im Prozess.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
          alt: "Spaltsiebzylinder mit Axialschlitz und Verstärkungsringen",
        },
      },
      specTable: {
        heading: "Technische Parameter der Axialschlitz-Zylinder",
        intro: "Fertigung nach Kundenmaß für selbstreinigende Filtergehäuse und Zentrifugenkörbe.",
        columns: [
          { key: "parameter", label: "Parameter", align: "left" },
          { key: "range", label: "Bereich", align: "left" },
          { key: "details", label: "Details", align: "left" },
        ],
        rows: [
          {
            parameter: "Spaltweite",
            range: "0.02 – 5.0 mm",
            details: "Kontinuierliche Längsspalte mit engen Fertigungstoleranzen",
          },
          {
            parameter: "Ausrichtung",
            range: "Parallel zur Achse",
            details: "V-Drähte axial angeordnet mit kreisförmigen Stützringen",
          },
          {
            parameter: "Strömungsrichtung",
            range: "FOTI / FITO",
            details: "Von außen nach innen oder von innen nach außen durchströmt",
          },
          {
            parameter: "Standard-Legierungen",
            range: "AISI 304, AISI 316, 316L",
            details: "Edelstahl für chemische Beständigkeit und hygienische Prozesse",
          },
          {
            parameter: "Durchmesserbereich",
            range: "25.4 – 1000 mm",
            details: "Passgenau abgestimmt auf die Abmessungen des Filterbehälters",
          },
        ],
        caption: "Werte basieren auf standardmäßigen Fertigungsgrenzen für widerstandsgeschweißte Axialschlitzzylinder.",
      },
      features: {
        heading: "Konstruktionsvorteile des Axialschlitzes",
        intro: "Entwickelt für Systeme, die kontinuierliche Filtration mit mechanischer Kuchenentfernung verbinden.",
        items: [
          {
            title: "Schlagfreier Schaberbetrieb",
            text: "Lineare Führungsbahnen eliminieren Schwingungen und sichern gleichmäßigen Kuchenabtrag.",
          },
          {
            title: "Umlaufende Stützringe",
            text: "Sichern maximale Formstabilität gegenüber Differenzdrücken und mechanischen Drehmomenten.",
          },
          {
            title: "Präzise Endanschlüsse",
            text: "Geschweißte Flansche und Passringe für exakte Ausrichtung im Gehäuse.",
          },
          {
            title: "Zuverlässige Abreinigung",
            text: "Nach innen erweiterte Spalte erleichtern das Ausspülen und senken den Spülmittelbedarf.",
          },
        ],
      },
      related: {
        heading: "Verwandte Spaltsiebzylinder",
        intro: "Vergleichen Sie axiale Zylinder mit Umfangsschlitz-Ausführungen oder wechseln Sie zur übergeordneten Kategorie.",
        cards: [
          {
            title: "Umfangsschlitz-Zylinder",
            text: "Profildrähte verlaufen kontinuierlich um den Zylinderumfang auf axialen Tragstäben, optimal für Trommelsiebe, Rotationsfilter und axialen Materialtransport.",
            to: "/CircumferentialSlotCylindersPage",
            badge: "Umlaufend",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
              alt: "Spaltsiebzylinder mit Umfangsschlitz für Trommelfiltration und Drehrohre",
            },
            linkLabel: "Zu den Umfangsschlitz-Zylindern",
          },
          {
            title: "Zylindrische Spaltsiebe Übersicht",
            text: "Gesamte Übersicht zu Bauformen, Fertigungsgrenzen und Einsatzbereichen zylindrischer Spaltsiebe.",
            to: "/CylindricalScreensPage",
            badge: "Hauptkategorie",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
              alt: "Industrielle zylindrische Edelstahl-Spaltsiebe und Filterstützkörbe",
            },
            linkLabel: "Zur Zylinder-Übersicht",
          },
        ],
      },
      cta: {
        id: "axial-slot-quote",
        eyebrow: "Technische Anfrage",
        heading: "Angebot für Axialschlitz-Zylinder anfordern",
        lead: "Senden Sie uns Maße, Spaltweite und Angaben zum Abstreifersystem für eine fundierte technische Auslegung.",
        secondaryLabel: "Zurück zu Zylindrische Spaltsiebe",
        secondaryTo: "/CylindricalScreensPage",
        note: "Werksprüfzeugnisse und PMI-Prüfungen auf Anforderung im Auftrag lieferbar.",
      },
    },
  },
  fr: {
    diagramCopy: {
      sectionTitle: "Principe Mécanique des Fentes Axiales",
      sectionLead: "Les fils profilés sont orientés selon l'axe longitudinal du cylindre, formant des rails continus qui permettent aux lames de racleur de glisser sans heurter les arêtes des fils.",
    },
    content: {
      breadcrumbKey: "AxialSlotCylinders",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Cylindres en Fil Profilé",
        standardBadge: "Fil en V Longitudinal",
        heading: "Cylindres à Fentes Axiales",
        lead: "Cylindres filtrants en fil profilé avec fentes continues parallèles à l'axe longitudinal. Conçus pour les systèmes de nettoyage à racleur mécanique automatique, les fortes pressions différentielles et la séparation solide-liquide continue.",
        secondaryCta: {
          label: "Voir Tous les Cylindres",
          to: "/CylindricalScreensPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
          alt: "Cylindre en fil profilé à fentes axiales et renforts longitudinaux",
        },
        cardNote: "Systèmes à Racleur · FOTI / FITO",
        cardLink: {
          label: "Demander un Devis",
          to: "/RequestAQuotePage",
        },
        specs: [
          { label: "Largeur de fente", value: "0.02 – 5.0 mm" },
          { label: "Orientation", value: "Parallèle à l'axe" },
          { label: "Sens d'écoulement", value: "Options FOTI / FITO" },
          { label: "Alliages standards", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Guidage Linéaire Continu et Préservation du Racleur",
        heading: "Fils en V Longitudinaux pour Systèmes à Racleur Mécanique",
        paragraphs: [
          "Les cylindres à fentes axiales MutualFilter orientent les fils en V parallèlement à l'axe longitudinal, soutenus à l'opposé par des anneaux de renfort circonférentiels. Cet agencement produit des pistes linéaires continues, permettant aux racleurs de détacher le gâteau de filtration sans vibrations ni accrochages.",
          "Dans un filtre autonettoyant à fentes circonférentielles, les lames heurteraient continuellement les arêtes des fils. La disposition axiale supprime ces impacts répétés, prolonge la tenue des lames et évite la fatigue mécanique des soudures de renfort.",
          "Configurables avec la face lisse de filtration à l'extérieur (FOTI) ou à l'intérieur (FITO). Le raclage interne est privilégié pour les peintures, résines et produits visqueux, tandis que le raclage externe est courant sur les filtres d'eau de process.",
        ],
        keyPoints: [
          "Les profils longitudinaux fournissent une surface de glissement linéaire protégeant les lames contre les chocs répétés.",
          "Les anneaux de renfort circonférentiels préservent l'écartement des fentes sous le couple de rotation du racleur.",
          "Options avec surface utile lisse intérieure ou extérieure pour convenir à tous les sens de circulation du fluide.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
          alt: "Vue rapprochée d'un cylindre à fentes axiales et anneaux de renfort",
        },
      },
      specTable: {
        heading: "Caractéristiques Techniques des Cylindres Axiaux",
        intro: "Fabrication sur mesure d'après plans pour intégration dans carters autonettoyants et paniers centrifuges.",
        columns: [
          { key: "parameter", label: "Caractéristique", align: "left" },
          { key: "range", label: "Plage Standard", align: "left" },
          { key: "details", label: "Détails Techniques", align: "left" },
        ],
        rows: [
          {
            parameter: "Largeur de fente",
            range: "0.02 – 5.0 mm",
            details: "Fentes longitudinales continues avec tolérances serrées",
          },
          {
            parameter: "Orientation",
            range: "Parallèle à l'axe",
            details: "Fils en V orientés longitudinalement sur anneaux circonférentiels",
          },
          {
            parameter: "Sens d'écoulement",
            range: "FOTI / FITO",
            details: "De l'extérieur vers l'intérieur ou de l'intérieur vers l'extérieur",
          },
          {
            parameter: "Alliages standards",
            range: "AISI 304, AISI 316, 316L",
            details: "Inox austénitique pour applications chimiques, industrielles et alimentaires",
          },
          {
            parameter: "Diamètres",
            range: "25.4 – 1000 mm",
            details: "Adaptation précise aux corps de filtres existants",
          },
        ],
        caption: "Données conformes aux limites de fabrication des fils profilés soudés par résistance.",
      },
      features: {
        heading: "Avantages de la Conception à Fentes Axiales",
        intro: "Conçus pour les équipements alliant filtration continue et décolmatage mécanique par raclage.",
        items: [
          {
            title: "Glissement sans Choc",
            text: "Le guidage linéaire élimine les à-coups et garantit un raclage uniforme du média.",
          },
          {
            title: "Anneaux de Renfort",
            text: "Offrent une tenue maximale contre l'écrasement radial et la déformation sous couple.",
          },
          {
            title: "Raccordements Usinés",
            text: "Brides et portées de joint soudées selon des tolérances strictes de concentricité.",
          },
          {
            title: "Régénération Facile",
            text: "L'élargissement intérieur des fentes favorise l'évacuation rapide des résidus filtrés.",
          },
        ],
      },
      related: {
        heading: "Cylindres Associés",
        intro: "Comparez les fentes axiales avec la géométrie circonférentielle ou retournez à la vue d'ensemble.",
        cards: [
          {
            title: "Cylindres à Fente Circonférentielle",
            text: "Les fils profilés s'enroulent autour de la circonférence sur des tiges axiales, conçus pour les trommels rotatifs, tambours et filtres à progression axiale.",
            to: "/CircumferentialSlotCylindersPage",
            badge: "Circonférentielle",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
              alt: "Cylindre en fil profilé à fente circonférentielle pour trommel rotatif",
            },
            linkLabel: "Voir les Cylindres Circonférentiels",
          },
          {
            title: "Cylindres Filtrants Général",
            text: "Consultez l'ensemble des gammes de cylindres en fil profilé, limites dimensionnelles et alliages.",
            to: "/CylindricalScreensPage",
            badge: "Catégorie Parente",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
              alt: "Tamis cylindriques industriels en fil profilé inox et éléments de renfort",
            },
            linkLabel: "Retour aux Cylindres Filtrants",
          },
        ],
      },
      cta: {
        id: "axial-slot-quote",
        eyebrow: "Étude et Dimensionnement",
        heading: "Obtenir une Offre pour Cylindres à Fentes Axiales",
        lead: "Renseignez diamètre, longueur, fente requise et caractéristiques du racleur pour une validation technique.",
        secondaryLabel: "Retour aux Cylindres Filtrants",
        secondaryTo: "/CylindricalScreensPage",
        note: "Certificats matière et contrôle de nuance disponibles sur demande dans la commande.",
      },
    },
  },
  pt: {
    diagramCopy: {
      sectionTitle: "Princípio Mecânico da Fenda Axial",
      sectionLead: "Os arames perfilados acompanham o eixo longitudinal do cilindro, criando trilhos lineares contínuos que permitem às lâminas do raspador deslizar sem golpear as bordas dos arames.",
    },
    content: {
      breadcrumbKey: "AxialSlotCylinders",
      relatedTopic: "wedge",
      hero: {
        categoryBadge: "Cilindros de Arame em Cunha",
        standardBadge: "Arame em V Longitudinal",
        heading: "Cilindros de Fenda Axial",
        lead: "Cilindros filtrantes de arame em cunha com fendas contínuas paralelas ao eixo longitudinal. Desenvolvidos para sistemas autolimpantes com raspadores mecânicos, suporte a pressões diferenciais elevadas e separação sólido-líquido contínua.",
        secondaryCta: {
          label: "Ver Todos os Cilindros",
          to: "/CylindricalScreensPage",
        },
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
          alt: "Cilindro de arame em cunha com fenda axial e perfis longitudinais",
        },
        cardNote: "Sistemas Raspadores · FOTI / FITO",
        cardLink: {
          label: "Solicitar Cotação",
          to: "/RequestAQuotePage",
        },
        specs: [
          { label: "Abertura da fenda", value: "0.02 – 5.0 mm" },
          { label: "Orientação", value: "Paralela ao eixo" },
          { label: "Configuração de fluxo", value: "Opções FOTI / FITO" },
          { label: "Ligas padrão", value: "AISI 304 / 316L" },
        ],
      },
      overview: {
        eyebrow: "Guia Linear Contínua e Proteção do Raspador",
        heading: "Orientação Longitudinal de Arame em V para Raspagem Mecânica",
        paragraphs: [
          "Os cilindros de fenda axial MutualFilter posicionam os arames em V paralelamente ao eixo longitudinal do cilindro, sustentados externamente por anéis circunferenciais de reforço. Essa disposição produz pistas lineares contínuas, permitindo que as lâminas raspadoras removam a torta de filtração sem vibrações nem impactos.",
          "Em filtros com raspadores mecânicos operando sobre fendas circunferenciais, o raspador cruzaria repetidamente as arestas dos arames, causando desgaste precoce da lâmina e fadiga nas soldas. A orientação axial elimina esses impactos, aumentando a durabilidade do raspador e do elemento filtrante.",
          "Disponíveis com face de trabalho lisa no exterior (FOTI) ou no interior (FITO). O raspador interno é muito comum em tintas, resinas e fluidos viscosos, enquanto o raspador externo atende filtros autolimpantes para água de captação e processos industriais.",
        ],
        keyPoints: [
          "Perfis longitudinais criam uma guia lisa que protege as lâminas do raspador contra desgastes por impacto repetido.",
          "Anéis de sustentação circunferenciais garantem estabilidade dimensional da fenda sob o torque mecânico do raspador.",
          "Opções com superfície lisa interna ou externa para acomodar fluxos centrípetos ou centrífugos no processo.",
        ],
        image: {
          src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-an-ax-45d1d0.webp",
          alt: "Detalhe de cilindro de fenda axial com arames longitudinais e anéis",
        },
      },
      specTable: {
        heading: "Especificações Técnicas dos Cilindros Axiais",
        intro: "Construção sob medida conforme desenho para filtros autolimpantes e cestos centrífugos industriais.",
        columns: [
          { key: "parameter", label: "Parâmetro", align: "left" },
          { key: "range", label: "Faixa Padrão", align: "left" },
          { key: "details", label: "Detalhes Técnicos", align: "left" },
        ],
        rows: [
          {
            parameter: "Abertura da fenda",
            range: "0.02 – 5.0 mm",
            details: "Fenda longitudinal contínua com tolerâncias micrométricas uniformes",
          },
          {
            parameter: "Orientação",
            range: "Paralela ao eixo",
            details: "Arames longitudinais em V apoiados em anéis circunferenciais de suporte",
          },
          {
            parameter: "Sentido do fluxo",
            range: "FOTI / FITO",
            details: "Fluxo de fora para dentro ou de dentro para fora",
          },
          {
            parameter: "Ligas padrão",
            range: "AISI 304, AISI 316, 316L",
            details: "Aço inoxidável de alta qualidade para processos sanitários e industriais",
          },
          {
            parameter: "Diâmetro",
            range: "25.4 – 1000 mm",
            details: "Fabricação personalizada para encaixe no vaso do filtro autolimpante",
          },
        ],
        caption: "Dados técnicos baseados nas capacidades de solda por resistência para fenda axial.",
      },
      features: {
        heading: "Vantagens do Projeto de Fenda Axial",
        intro: "Concebido para processos com filtração contínua e raspagem mecânica automatizada da torta.",
        items: [
          {
            title: "Raspagem sem Trepidação",
            text: "O alinhamento longitudinal elimina impactos e assegura remoção uniforme do particulado.",
          },
          {
            title: "Anéis de Apoio Resistentes",
            text: "Oferecem suporte estrutural contra colapso sob pressão diferencial elevada.",
          },
          {
            title: "Terminações Sob Medida",
            text: "Flanges e anéis usinados soldados com estrito controle dimensional para vedação precisa.",
          },
          {
            title: "Fácil Retrolavagem",
            text: "Abertura que se alarga para dentro permite expulsão ágil dos resíduos retidos na superfície.",
          },
        ],
      },
      related: {
        heading: "Cilindros Filtrantes Relacionados",
        intro: "Compare a estrutura de fenda axial com os cilindros de fenda circunferencial.",
        cards: [
          {
            title: "Cilindros de Fenda Circunferencial",
            text: "Arames de superfície enrolados continuamente na circunferência sobre barras axiais de suporte, projetados para trommels rotativos e cilindros de alimentação interna.",
            to: "/CircumferentialSlotCylindersPage",
            badge: "Circunferencial",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-a-cir-c1a7f2.webp",
              alt: "Cilindro de arame em cunha com ranhura circunferencial para trommel rotativo",
            },
            linkLabel: "Ver Cilindros Circunferenciais",
          },
          {
            title: "Visão Geral de Cilindros Filtrantes",
            text: "Consulte todas as opções de cilindros de arame em cunha, faixas dimensionais e ligas metálicas.",
            to: "/CylindricalScreensPage",
            badge: "Categoria Principal",
            image: {
              src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-product-photograph-of-sever-9a20f1.png",
              alt: "Telas filtrantes cilíndricas industriais de arame em cunha e tubos de suporte",
            },
            linkLabel: "Voltar para Cilindros Filtrantes",
          },
        ],
      },
      cta: {
        id: "axial-slot-quote",
        eyebrow: "Consulta Técnica",
        heading: "Solicite Cotação para Cilindros de Fenda Axial",
        lead: "Informe diâmetro, comprimento, abertura de fenda e detalhes do raspador para nossa equipe de engenharia.",
        secondaryLabel: "Voltar para Cilindros Filtrantes",
        secondaryTo: "/CylindricalScreensPage",
        note: "Certificados de ensaio de material e identificação positiva disponíveis sob pedido no pedido.",
      },
    },
  },
};
